'use client';

/**
 * Invoice endpoints for the global desk.
 *
 * Routes mounted under `/billing/`:
 *   GET    /billing/invoices
 *   GET    /billing/invoices/{id}
 *   GET    /billing/invoices/{id}/download   — returns PDF bytes (or HTML fallback)
 *
 * The backend picks the table from the org's edition
 * (billing_invoices/invoices.py), so a global org is served commercial
 * invoices only (series GLX-…, its own currency, no computed tax). The India
 * GST customer-details hooks (`/billing/gst-details`) were removed from this
 * build: that endpoint 404s for a global org (§1A).
 */
import { useMutation, useQuery } from '@tanstack/react-query';

import { useApi } from '@/lib/api';

export type InvoicePaymentStatus = 'paid' | 'pending' | 'failed' | 'refunded' | string;

/**
 * Single invoice row — list and detail share this shape
 * (billing_invoices/invoices.py::InvoiceOut, commercial-invoice fields). The
 * shared schema also declares India tax-invoice fields; they are always null
 * for a global org and are deliberately not typed here.
 */
export interface Invoice {
  id: string;
  document_type?: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  issued_at: string;
  customer_legal_name: string;
  description: string;
  currency: string;
  subtotal: number;
  tax_total: number;
  total: number;
  customer_country: string | null;
  customer_tax_id: string | null;
  tax_label: string | null;
  tax_treatment: string | null;
  tax_note: string | null;
  payment_status: InvoicePaymentStatus;
  paid_at: string | null;
}

export const INVOICES_QUERY_KEY = ['billing', 'invoices'] as const;
export const invoiceDetailKey = (id: string) => ['billing', 'invoices', 'detail', id] as const;

/** GET /billing/invoices — newest first. */
export function useInvoices() {
  const api = useApi();
  return useQuery<readonly Invoice[], Error>({
    queryKey: INVOICES_QUERY_KEY,
    queryFn: ({ signal }) => api<readonly Invoice[]>('/billing/invoices', { signal }),
    staleTime: 60_000,
  });
}

/** GET /billing/invoices/{id}. */
export function useInvoice(invoiceId: string | null) {
  const api = useApi();
  return useQuery<Invoice, Error>({
    queryKey: invoiceDetailKey(invoiceId ?? '__none__'),
    queryFn: ({ signal }) => api<Invoice>(`/billing/invoices/${invoiceId}`, { signal }),
    enabled: invoiceId !== null,
    staleTime: 5 * 60_000,
  });
}

/**
 * GET /billing/invoices/{id}/download — returns the PDF inline (or HTML if
 * WeasyPrint isn't available on the server).  Returns the raw `Response`
 * via `raw: true`; the caller is responsible for blob → object URL +
 * `<a download>` triggering.
 */
export function useDownloadInvoice() {
  const api = useApi();
  return useMutation<{ blob: Blob; filename: string; contentType: string }, Error, string>({
    mutationFn: async (invoiceId) => {
      const res = await api<Response>(`/billing/invoices/${invoiceId}/download`, {
        method: 'GET',
        raw: true,
      });
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match?.[1] ?? `invoice-${invoiceId}.pdf`;
      return {
        blob,
        filename,
        contentType: res.headers.get('content-type') ?? 'application/pdf',
      };
    },
  });
}
